'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, type SxProps, Typography } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';
import { GOTHAM } from '../../theme';
import { AdvertiserDisclosureMessage } from './AdvertiserDisclosureMessage';
import { debounce } from '@cc-shopping/utils/function/debounce';
import { type AdvertiserDisclosurePosition, getPositionProps, getTailStyles } from './utils';
export const LABEL = 'Advertiser disclosure';
const PANEL_WIDTH = 576;
const PANEL_MAX_WIDTH = `min(90vw, ${PANEL_WIDTH}px)`;
const VIEWPORT_INSET = 16;
export const AdvertiserDisclosure = t0 => {
  const $ = _c(40);
  const {
    bold: t1,
    color: t2,
    containerStyles,
    position: t3,
    shouldCloseOnWidthResizing: t4,
    labelStyles
  } = t0;
  const bold = t1 === undefined ? false : t1;
  const color = t2 === undefined ? "secondary.main" : t2;
  const position = t3 === undefined ? "bottom-right" : t3;
  const shouldCloseOnWidthResizing = t4 === undefined ? false : t4;
  const id = useId();
  let t5;
  if ($[0] !== id) {
    let t6;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = /[^a-zA-Z0-9]/g;
      $[2] = t6;
    } else {
      t6 = $[2];
    }
    t5 = id.replace(t6, "");
    $[0] = id;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const sanitizedId = t5;
  const popoverId = `ad-${sanitizedId}`;
  const anchorName = `--ad-${sanitizedId}`;
  const popoverRef = useRef(null);
  const [supportsPopoverTarget, setSupportsPopoverTarget] = useState(true);
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setSupportsPopoverTarget("showPopover" in HTMLElement.prototype && "popoverTargetElement" in HTMLButtonElement.prototype);
    };
    t7 = [];
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[5] !== shouldCloseOnWidthResizing) {
    t8 = () => {
      if (!shouldCloseOnWidthResizing) {
        return;
      }
      let lastWidth = window.innerWidth;
      const handleResize = debounce(() => {
        const currentWidth = window.innerWidth;
        if (currentWidth !== lastWidth) {
          lastWidth = currentWidth;
          popoverRef.current?.hidePopover();
        }
      }, 100);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t9 = [shouldCloseOnWidthResizing];
    $[5] = shouldCloseOnWidthResizing;
    $[6] = t8;
    $[7] = t9;
  } else {
    t8 = $[6];
    t9 = $[7];
  }
  useEffect(t8, t9);
  let t10;
  if ($[8] !== position) {
    t10 = getTailStyles(position);
    $[8] = position;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  const tailStyles = t10;
  const {
    isTopPosition,
    horizontalAlignment
  } = getPositionProps(position);
  const translateX = horizontalAlignment === "left" ? "0" : horizontalAlignment === "center" ? "-50%" : "-100%";
  const translateY = isTopPosition ? "calc(-100% - var(--ad-tail-size))" : "0";
  const anchorLeft = horizontalAlignment === "left" ? "anchor(left)" : horizontalAlignment === "center" ? "anchor(50%)" : "anchor(right)";
  const anchorToPanelLeft = horizontalAlignment === "left" ? "0px" : horizontalAlignment === "center" ? `min(45vw, ${PANEL_WIDTH / 2}px)` : PANEL_MAX_WIDTH;
  const left = `clamp(calc(${VIEWPORT_INSET}px + ${anchorToPanelLeft}), ${anchorLeft}, calc(100% - ${VIEWPORT_INSET}px - ${PANEL_MAX_WIDTH} + ${anchorToPanelLeft}))`;
  let t11;
  if ($[10] !== containerStyles) {
    t11 = {
      position: "relative",
      display: "inline-block",
      ...containerStyles
    };
    $[10] = containerStyles;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== popoverId || $[13] !== supportsPopoverTarget) {
    t12 = supportsPopoverTarget ? {
      popoverTarget: popoverId
    } : {
      onClick: () => {
        popoverRef.current?.showPopover();
      }
    };
    $[12] = popoverId;
    $[13] = supportsPopoverTarget;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== anchorName) {
    t13 = {
      cursor: "pointer",
      anchorName
    };
    $[15] = anchorName;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const t14 = bold ? 500 : 400;
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      textDecoration: "underline"
    };
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== color || $[19] !== labelStyles || $[20] !== t14) {
    t16 = <Typography variant="bodySmall" title="Advertiser Disclosure" component="span" sx={{
      fontWeight: t14,
      cursor: "pointer",
      color,
      "&:hover": t15,
      fontFamily: GOTHAM,
      ...labelStyles
    }}>{LABEL}</Typography>;
    $[18] = color;
    $[19] = labelStyles;
    $[20] = t14;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t12 || $[23] !== t13 || $[24] !== t16) {
    t17 = <Box component="button" {...t12} sx={t13}>{t16}</Box>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t16;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = isTopPosition ? "anchor(top)" : "calc(anchor(bottom) + var(--ad-tail-size))";
  const t19 = `${translateX} ${translateY}`;
  let t20;
  if ($[26] !== anchorName || $[27] !== left || $[28] !== t18 || $[29] !== t19 || $[30] !== tailStyles) {
    t20 = {
      positionAnchor: anchorName,
      top: t18,
      left,
      translate: t19,
      ...tailStyles,
      margin: 0,
      width: PANEL_WIDTH,
      maxWidth: PANEL_MAX_WIDTH,
      backgroundColor: "white",
      color: "common.black",
      border: "1px solid",
      borderColor: "grey.300",
      borderRadius: 0,
      boxShadow: "0 4px 8px 0 rgb(100 102 106 / 15%), 0 8px 16px 0 rgb(100 102 106 / 33%)",
      padding: "1rem 1rem 0.5rem 1rem",
      overflow: "visible",
      textAlign: "left"
    };
    $[26] = anchorName;
    $[27] = left;
    $[28] = t18;
    $[29] = t19;
    $[30] = tailStyles;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <AdvertiserDisclosureMessage />;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== popoverId || $[34] !== t20) {
    t22 = <Box ref={popoverRef} popover="auto" id={popoverId} aria-label={`${LABEL} Modal`} sx={t20}>{t21}</Box>;
    $[33] = popoverId;
    $[34] = t20;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t11 || $[37] !== t17 || $[38] !== t22) {
    t23 = <Box component="span" sx={t11}>{t17}{t22}</Box>;
    $[36] = t11;
    $[37] = t17;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  return t23;
};